// Named imports only — a namespace import (`import * as Sentry`) references every
// export, which prevents webpack from tree-shaking out `lazyLoadIntegration`. That
// helper builds a `https://browser.sentry-cdn.com/.../*.min.js` URL and loads it at
// runtime, which Chrome Web Store flags as remotely-hosted code (MV3 rejection
// "Blue Argon"). Importing only what we use lets the CDN loader drop from the bundle.
import { init, browserTracingIntegration, captureException } from '@sentry/react';
// Named JSON import: Vite inlines only `version`, not the whole manifest.
import { version as manifestVersion } from '../manifest.json';
import { isNativeApp, nativeAppInfo } from './nativeApp';
import { isSafariExtension } from './browserApi';

export type SentryPlatform = 'ios' | 'safari' | 'chrome' | 'web';

/** Everything the init decision depends on, so it can be tested without a DOM. */
export interface SentryRuntime {
  dsn: string | undefined;
  nodeEnv: string | undefined;
  /** CI-injected release (git SHA); wins over the manifest version when set. */
  ciRelease: string | undefined;
  manifestVersion: string;
  /** iOS host's own version + build (nativeAppInfo); null off-native or on old builds. */
  nativeVersion: { appVersion: string; build: string } | null;
  protocol: string;
  hostname: string;
  native: boolean;
  safariExtension: boolean;
}

export interface SentryConfig {
  dsn: string;
  environment: 'production';
  release: string;
  platform: SentryPlatform;
}

// Screenshot, reel and preview rigs serve production builds from localhost.
const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]']);

/**
 * Null means "don't init". `.env.local` carries the prod DSN, so the DSN alone
 * is not enough: the Vite dev server and local rigs would report into prod.
 */
export function resolveSentryConfig(rt: SentryRuntime): SentryConfig | null {
  if (!rt.dsn) return null;
  if (rt.nodeEnv !== 'production') return null;
  if (LOCAL_HOSTS.has(rt.hostname)) return null;

  const platform: SentryPlatform = rt.native
    ? 'ios'
    : rt.safariExtension
      ? 'safari'
      : rt.protocol === 'chrome-extension:'
        ? 'chrome'
        : 'web';

  // iOS ships on its own version line (App Store 1.0.12 while Chrome is 1.0.24), so
  // the manifest version would give every iOS build the same release and
  // "resolved in next release" could never catch an iOS regression. Use the host's
  // version + build there.
  const release = rt.ciRelease
    || (platform === 'ios' && rt.nativeVersion?.appVersion
      ? `rhemn-ios@${rt.nativeVersion.appVersion}+${rt.nativeVersion.build}`
      : `rhemn@${rt.manifestVersion}+${platform}`);

  return {
    dsn: rt.dsn,
    environment: 'production',
    release,
    platform,
  };
}

export function initSentry(): void {
  const config = resolveSentryConfig({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    nodeEnv: process.env.NODE_ENV,
    ciRelease: process.env.REACT_APP_VERSION,
    manifestVersion,
    nativeVersion: (() => {
      const info = nativeAppInfo();
      return info?.appVersion ? { appVersion: info.appVersion, build: String(info.build ?? '') } : null;
    })(),
    protocol: window.location.protocol,
    hostname: window.location.hostname,
    native: isNativeApp(),
    safariExtension: isSafariExtension(),
  });
  if (!config) return;

  init({
    dsn: config.dsn,
    environment: config.environment,
    release: config.release,
    // Chrome, Safari and iOS share one bundle; the tag is the only way to split them
    initialScope: { tags: { platform: config.platform } },
    // Capture 100% of errors; sample traces at 10% to keep quotas low
    tracesSampleRate: 0.1,
    // Only send errors — not console noise
    integrations: [browserTracingIntegration()],
    // Strip PII from breadcrumbs
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === 'xhr' || breadcrumb.category === 'fetch') {
        // Don't log request bodies
        if (breadcrumb.data) {
          delete breadcrumb.data.body;
        }
      }
      return breadcrumb;
    },
  });
}

// Re-export a minimal surface so callers keep using `Sentry.captureException(...)`
// without a namespace import pulling the whole SDK (and the CDN loader) back in.
export const Sentry = { captureException };
